import BigNumber from 'bignumber.js'
import FormValidator from 'config/constants/formValidator'
import { USD_CURRENCY } from 'config/constants/tokens'
import useForm from 'hooks/useForm'
import { useSelectedBalance } from 'layout/Components/BalanceSelect/hooks'
import { useEffect, useMemo } from 'react'
import { useTokenUsdPrice } from 'state/app/hooks'

export const useFormBetAmount = ({ maxBet }: { maxBet: number }) => {
  const { selectedBalance } = useSelectedBalance()
  const tokenInUsdPrice = useTokenUsdPrice(selectedBalance.token)

  const balanceInUsd = useMemo(() => {
    return new BigNumber(selectedBalance.amount || '0').multipliedBy(tokenInUsdPrice)
  }, [tokenInUsdPrice.toString(), selectedBalance.amount.toString()])

  const { states, controls, isValid, validate } = useForm({
    amount: {
      validators: [
        FormValidator.lt(0),
        FormValidator.max(balanceInUsd),
        maxBet > 0 ? FormValidator.gt(maxBet) : () => null,
      ],
      value: '',
      formatValue(value) {
        const parts = String(value)?.split('.')
        if (parts && parts[1]) {
          parts[1] = parts[1].substr(0, 2)
        }

        const formatDecimal = parts[1] && Number(parts[1]) >= 0 && `.${parts[1]}`

        return formatDecimal ? `${parts[0]}${formatDecimal}` : value
      },
    },
  })

  const estimateAmountInToken = useMemo(() => {
    return new BigNumber(states.amount.value || '0').div(tokenInUsdPrice)
  }, [tokenInUsdPrice])

  useEffect(() => {
    if (states.amount.isDirty) {
      validate('amount')
    }
  }, [tokenInUsdPrice.toString(10), selectedBalance.amount.toString(10)])

  return useMemo(
    () => ({
      states,
      controls,
      isValid,
      balanceAmount: {
        token: USD_CURRENCY,
        amount: balanceInUsd,
      },
      tokenAmount: {
        token: USD_CURRENCY,
        amount: new BigNumber(states.amount.value),
      },
      estimateAmountInToken,
    }),
    [states, controls, isValid, selectedBalance, balanceInUsd, estimateAmountInToken],
  )
}
